import { useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import type { BookingStage, BookingTerms } from 'api-client/models';
import { useAppContext } from '@/context/AppContext';
import { bookingError } from '../utils/bookingError';
import { BookingTermsFields, emptyBookingTerms } from './BookingTermsFields';

export default function BookingRequestForm() {
  const { apiClients, team } = useAppContext();
  const navigate = useNavigate();
  const [stages, setStages] = useState<BookingStage[]>([]);
  const [stage, setStage] = useState('');
  const [search, setSearch] = useState('');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [terms, setTerms] = useState<BookingTerms>(emptyBookingTerms);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let current = true;
    setLoading(true);
    apiClients.stages.stagesBookingDirectory({ search }).then(result => {
      if (current) setStages(result.filter(item => item.hostTeam !== team?.id));
    }).catch(() => { if (current) setError('Unable to load venues. Try searching again.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [apiClients.stages, team?.id, search]);

  const submit = async (): Promise<void> => {
    if (!team || !stage || !title.trim() || !date) {
      setError('Enter a title, stage and requested date.'); return;
    }
    setPending(true); setError('');
    try {
      const saved = await apiClients.holds.holdsCreate({ holdCreateUpdate: {
        title: title.trim(), stage, promoterTeam: team.id, holdDate: new Date(`${date}T00:00:00Z`), bookingTerms: terms,
      } });
      navigate(`/bookings/${saved.id}`);
    } catch (reason) {
      setError(await bookingError(reason));
    } finally { setPending(false); }
  };
  return <Box sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, md: 3 } }}>
    <Stack spacing={3} component="form" onSubmit={event => { event.preventDefault(); void submit(); }}>
      <Typography component="h1" variant="h4">Request a host venue</Typography>
      <Alert severity="info">The host must accept before your request reserves a queue position. Proposed terms are shared with the host.</Alert>
      <TextField label="Show title" value={title} required disabled={pending} onChange={event => setTitle(event.target.value)} />
      <TextField label="Search venues and stages" value={search} disabled={pending} onChange={event => setSearch(event.target.value)}
        helperText="Showing up to 100 matches. Refine your search to find a venue." />
      <TextField select label="Host stage" value={stage} required disabled={pending || loading || stages.length === 0}
        helperText={loading ? 'Loading stages…' : stages.length ? 'Venue details beyond this listing stay private.' : 'No matching external stages.'}
        onChange={event => setStage(event.target.value)}>
        {stages.map(item => <MenuItem key={item.id} value={item.id}>{item.venueName} — {item.name}</MenuItem>)}
      </TextField>
      <TextField label="Requested date" type="date" value={date} required disabled={pending} InputLabelProps={{ shrink: true }}
        onChange={event => setDate(event.target.value)} />
      <BookingTermsFields value={terms} onChange={setTerms} disabled={pending} />
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Button type="submit" variant="contained" disabled={pending || loading}>{pending ? 'Sending request…' : 'Send booking request'}</Button>
        <Button component={Link} to="/bookings" disabled={pending}>Cancel</Button>
      </Stack>
    </Stack>
  </Box>;
}
